import { lumiApi } from "../index";
import { Doc } from "../../../utils/Docs/Docs";
import { Concept } from "../../../utils/Docs/Concepts";

export const projectsApi = lumiApi.injectEndpoints({
    endpoints: (build) => ({
        // Existing individual endpoints
        getExactDocuments: build.mutation({
            query: ({
                id,
                filter,
                search,
                limit = 50,
                include_sentiment_on_concepts = false,
            }) => ({
                url: `/projects/${id}/docs/`,
                method: "POST",
                body: {
                    filter,
                    match_type: "exact",
                    search: { texts: [...search] },
                    limit,
                },
                include_sentiment_on_concepts,
            }),
            transformResponse: (response) =>
                response.result
                    .filter((doc) => doc.title?.trim() || doc.text.trim())
                    .map(Doc.fromJSON),
        }),
        getConceptualDocuments: build.mutation({
            query: ({
                id,
                filter,
                search,
                limit = 50,
                include_sentiment_on_concepts = false,
            }) => ({
                url: `/projects/${id}/docs/`,
                method: "POST",
                body: {
                    filter,
                    match_type: "conceptual",
                    search: { texts: [...search] },
                    limit,
                },
                include_sentiment_on_concepts,
            }),
            transformResponse: (response) =>
                response.result
                    .filter((doc) => doc.title?.trim() || doc.text.trim())
                    .map(Doc.fromJSON),
        }),
        // New combined endpoint
        getDocuments: build.mutation({
            queryFn: async (
                {
                    id,
                    filter,
                    search,
                    limit = 50,
                    include_sentiment_on_concepts = false,
                },
                queryApi,
                extraOptions,
                baseQuery
            ) => {
                try {
                    // Perform both requests concurrently
                    const [exactResponse, conceptualResponse] =
                        await Promise.all([
                            baseQuery({
                                url: `/projects/${id}/docs/`,
                                method: "POST",
                                body: {
                                    filter,
                                    match_type: "exact",
                                    search: { texts: [...search] },
                                    limit,
                                    include_sentiment_on_concepts,
                                },
                            }),
                            baseQuery({
                                url: `/projects/${id}/docs/`,
                                method: "POST",
                                body: {
                                    match_type: "conceptual",
                                    search: { texts: [...search] },
                                    limit,
                                    filter,
                                    include_sentiment_on_concepts,
                                },
                            }),
                        ]);

                    // Handle possible errors
                    if (exactResponse.error)
                        throw new Error(exactResponse.error);
                    if (conceptualResponse.error)
                        throw new Error(conceptualResponse.error);

                    // Combine and map data
                    const combinedData = [
                        ...exactResponse.data.result,
                        ...conceptualResponse.data.result,
                    ]
                        .filter((doc) => doc.title?.trim() || doc.text.trim())
                        .map(Doc.fromJSON);
                    const concept = Concept.fromJSON(exactResponse.data.search);
                    return { data: { documents: combinedData, concept } };
                } catch (error) {
                    return { error: error.message || "Unknown error" };
                }
            },
        }),
    }),
});

export const {
    useGetExactDocumentsMutation,
    useGetConceptualDocumentsMutation,
    useGetDocumentsMutation,
} = projectsApi;
